<template>
  <VContainer
    class="sitemap-page pt-10"
    fluid
  >
    <div class="sitemap-page__inner breakpoint-xl mx-auto">
      <a
        href="#sitemap-content"
        class="sitemap-page__skip-link"
      >
        {{ $t('sitemapPage.skipToSitemap') }}
      </a>

      <main id="sitemap-content">
        <h1 class="sitemap-page__title">
          {{ $t('sitemapPage.title') }}
        </h1>
        <p class="sitemap-page__description">
          {{ $t('sitemapPage.description') }}
        </p>

        <div
          v-if="loading"
          class="sitemap-page__loading"
          aria-live="polite"
          aria-busy="true"
        >
          {{ $t('sitemapPage.loading') }}
        </div>

        <div
          v-else-if="error"
          class="sitemap-page__error"
          role="alert"
        >
          {{ error }}
        </div>

        <nav
          v-else-if="groups.length"
          :aria-label="$t('sitemapPage.navAriaLabel')"
          class="sitemap-page__nav"
        >
          <ul class="sitemap-page__group-list">
            <li
              v-for="group in groups"
              :key="group.label"
              class="sitemap-page__group-item"
            >
              <h2 class="sitemap-page__group-heading">
                {{ group.label }}
              </h2>
              <ul class="sitemap-page__url-list">
                <li
                  v-for="entry in group.entries"
                  :key="entry.url"
                  class="sitemap-page__url-item"
                >
                  <a
                    :href="entry.url"
                    class="sitemap-page__link"
                  >
                    {{ entry.label }}
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </main>
    </div>
  </VContainer>
</template>

<script lang="ts">
import { getMetaInfo } from '@/utils/seo';

export default {
  name: 'SitemapPage',
  metaInfo() {
    return getMetaInfo({ title: (this as any).$t('sitemapPage.title') });
  },
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from '@/composables';

const { t } = useI18n();
const loading = ref(true);
const error = ref<string | null>(null);
const groups = ref<Array<{ label: string; entries: Array<{ url: string; label: string }> }>>([]);

/**
 * Sitemap XML from CMS may list another host; links should stay on the app the user is on.
 */
function toCurrentOriginHref(remoteUrl: string) {
  const { origin } = globalThis.location;
  try {
    const parsed = new URL(remoteUrl, origin);
    return `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return remoteUrl;
  }
}

function getStrapiSitemapUrl() {
  // eslint-disable-next-line no-undef
  const clusterHost = config.VUE_APP_CLUSTER_HOST || process.env.VUE_APP_CLUSTER_HOST;
  // eslint-disable-next-line no-undef
  const strapiUrl = config.VUE_APP_STRAPI_URL || process.env.VUE_APP_STRAPI_URL || '';
  // eslint-disable-next-line no-undef
  const strapiClusterUrl = config.VUE_APP_STRAPI_CLUSTER_URL || process.env.VUE_APP_STRAPI_CLUSTER_URL || strapiUrl;

  const { hostname } = new URL(globalThis.location.href);
  const baseUrl = hostname === clusterHost ? strapiClusterUrl : strapiUrl;

  return `${baseUrl}/api/sitemap/index.xml`;
}

function formatLabel(segment: string) {
  return segment
    .replaceAll('-', ' ')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function groupUrls(urls: string[]) {
  const map = new Map<string, Array<{ url: string; label: string }>>();

  urls.forEach((url) => {
    const href = toCurrentOriginHref(url);
    let pathname;
    try {
      pathname = new URL(href).pathname;
    } catch {
      pathname = url;
    }

    const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
    const topSegment = segments[0] || '';

    const homeLabel = String(t('sitemapPage.home'));
    const groupLabel = formatLabel(topSegment) || homeLabel;
    const entryLabel = segments.length > 1
      ? segments.slice(1).map(formatLabel).join(' › ')
      : formatLabel(topSegment) || homeLabel;

    if (!map.has(groupLabel)) {
      map.set(groupLabel, []);
    }
    map.get(groupLabel)!.push({ url: href, label: entryLabel });
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, entries]) => ({ label, entries }));
}

function parseSitemap(xmlText: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  const ns = 'http://www.sitemaps.org/schemas/sitemap/0.9';

  const urlNodes = doc.getElementsByTagNameNS(ns, 'url');
  const locNodes = doc.getElementsByTagNameNS(ns, 'loc');

  // Handle sitemap index (collection of sub-sitemaps) vs regular sitemap
  const isIndex = !urlNodes.length && locNodes.length > 0;

  const rawUrls = isIndex
    ? Array.from(locNodes).map((loc) => (loc.textContent || '').trim())
    : Array.from(urlNodes)
      .map((url) => {
        const loc = url.getElementsByTagNameNS(ns, 'loc')[0];
        return loc ? (loc.textContent || '').trim() : null;
      })
      .filter(Boolean) as string[];

  return groupUrls(rawUrls);
}

async function fetchSitemapText() {
  // Primary: nginx-cached copy served same-origin (avoids CORS entirely)
  try {
    const response = await fetch('/sitemap.xml');
    if (response.ok) {
      return response.text();
    }
  } catch {
    // not available locally, fall through to direct Strapi fetch
  }

  // Fallback: fetch directly from Strapi (requires CORS to be open on the CMS)
  const strapiUrl = getStrapiSitemapUrl();
  const response = await fetch(strapiUrl);

  if (!response.ok) {
    throw new Error(`Failed to load sitemap (HTTP ${response.status})`);
  }

  return response.text();
}

async function fetchSitemap() {
  loading.value = true;
  error.value = null;

  try {
    const text = await fetchSitemapText();
    groups.value = parseSitemap(text);
  } catch (e) {
    error.value = String(t('sitemapPage.errorLoadFailed'));
    console.error('[SitemapPage]', e);
  } finally {
    loading.value = false;
  }
}

fetchSitemap();
</script>

<style lang="scss" scoped>
.sitemap-page {
  min-height: 400px;

  &__inner {
    padding-bottom: 6rem;
  }

  &__skip-link {
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;

    &:focus {
      position: static;
      width: auto;
      height: auto;
      margin-bottom: 1rem;
      display: inline-block;
    }
  }

  &__title {
    font-size: 2.8rem;
    font-weight: 700;
    margin-bottom: 0.8rem;
  }

  &__description {
    font-size: 1.6rem;
    margin-bottom: 3.2rem;
    color: #555;
  }

  &__loading,
  &__error {
    font-size: 1.6rem;
    padding: 1.6rem 0;
  }

  &__error {
    color: #c0392b;
  }

  &__group-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(26rem, 1fr));
    gap: 3.2rem;
  }

  &__group-item {
    break-inside: avoid;
  }

  &__group-heading {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 1.2rem;
    padding-bottom: 0.6rem;
    border-bottom: 2px solid #e0e0e0;
  }

  &__url-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__url-item {
    margin-bottom: 0.8rem;
  }

  &__link {
    font-size: 1.5rem;
    color: var(--v-secondary-base);
    text-decoration: underline;
    word-break: break-word;

    &:hover {
      color: var(--v-textSecondary-base);
    }

    &:focus {
      outline: 3px solid var(--v-secondary-base);
      outline-offset: 2px;
      border-radius: 2px;
    }

    &:visited {
      color: #5a1e96;
    }
  }
}
</style>
